import type { ParsedAngle } from "@/lib/tool-kit/geometry/angle-input";
import {
  addExact,
  type Exact,
  exactPi,
  exactRational,
  exactSqrt,
  mulExact,
  scaleExact,
  toNumberExact,
} from "@/lib/tool-kit/geometry/exact";
import {
  DEFAULT_FORMAT,
  decimalText,
  exactText,
  type ResultFormat,
  sub,
  text,
} from "@/lib/tool-kit/geometry/working";
import {
  addR,
  divR,
  formatFraction,
  intR,
  mulR,
  type Rational,
  signR,
  subR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";

/**
 * Plane-figure areas, kept exact.
 *
 * Every measurement arrives as a rational number already converted to one length unit,
 * so a rectangle of 2.5 by 4 has area 10, a circle-derived figure has area in terms of
 * π, and a Heron triangle with sides 4, 5, 6 has area 15√7/4 — with the decimal
 * derived from the exact form, never fed back into it.
 */

export const AREA_SHAPE_IDS = [
  "square",
  "rectangle",
  "triangle",
  "triangle3",
  "parallelogram",
  "trapezoid",
  "rhombus",
  "polygon",
  "ellipse",
  "annulus",
  "sector",
] as const;

export type AreaShapeId = (typeof AREA_SHAPE_IDS)[number];

export type LengthFieldId =
  | "side"
  | "length"
  | "width"
  | "base"
  | "height"
  | "sideA"
  | "sideB"
  | "sideC"
  | "base1"
  | "base2"
  | "diag1"
  | "diag2"
  | "semiA"
  | "semiB"
  | "outer"
  | "inner"
  | "radius";

/** The length measurements each shape asks for, in the order they are shown. */
export const AREA_SHAPE_FIELDS: Record<AreaShapeId, readonly LengthFieldId[]> =
  {
    square: ["side"],
    rectangle: ["length", "width"],
    triangle: ["base", "height"],
    triangle3: ["sideA", "sideB", "sideC"],
    parallelogram: ["base", "height"],
    trapezoid: ["base1", "base2", "height"],
    rhombus: ["diag1", "diag2"],
    polygon: ["side"],
    ellipse: ["semiA", "semiB"],
    annulus: ["outer", "inner"],
    sector: ["radius"],
  };

export type MetricKey =
  | "perimeter"
  | "diagonal"
  | "semiperimeter"
  | "midsegment"
  | "sideLength"
  | "apothem"
  | "circumradius"
  | "interiorAngle"
  | "centralAngle"
  | "perimeterApprox"
  | "width"
  | "arcLength"
  | "angleDegrees";

export interface Metric {
  key: MetricKey;
  /** The exact form, or null when only a decimal exists. */
  exact: Exact | null;
  value: number;
  /** Lengths carry the unit; angles are in degrees. */
  kind: "length" | "angle";
}

export interface AreaInputs {
  /** Measurements, already in one length unit. */
  lengths: Partial<Record<LengthFieldId, Rational>>;
  /** Number of sides of a regular polygon. */
  sides?: number;
  /** The sector's central angle. */
  angle?: ParsedAngle;
}

export type AreaError = "positive" | "triangle" | "sides" | "angle" | "annulus";

export interface AreaResult {
  ok: true;
  /** Exact area, or null when only the decimal is known (most regular polygons). */
  area: Exact | null;
  areaValue: number;
  metrics: Metric[];
  /** The formula with symbols, no numbers. */
  formula: string;
  /** The formula with the numbers put in, ending in the answer. */
  steps: string[];
}

export interface AreaFailure {
  ok: false;
  error: AreaError;
}

export const MIN_POLYGON_SIDES = 3;
export const MAX_POLYGON_SIDES = 1000;

const HALF: Rational = divR(intR(1), intR(2));
const FULL_TURN_DEGREES = 360;
const ANGLE_TOLERANCE = 1e-9;

type Draft = Omit<AreaResult, "ok">;
type Context = ResultFormat;

function lengthMetric(
  key: MetricKey,
  exact: Exact | null,
  value?: number
): Metric {
  return {
    key,
    exact,
    value: exact ? toNumberExact(exact) : (value ?? Number.NaN),
    kind: "length",
  };
}

function square(value: Rational): Rational {
  return mulR(value, value);
}

function exactArea(area: Exact): Pick<Draft, "area" | "areaValue"> {
  return { area, areaValue: toNumberExact(area) };
}

function squareArea(r: Record<string, Rational>, c: Context): Draft {
  const s = r.side;
  const area = exactRational(square(s));
  const diagonal = mulExact(exactRational(s), exactSqrt(intR(2)));
  return {
    ...exactArea(area),
    metrics: [
      lengthMetric("perimeter", exactRational(mulR(intR(4), s))),
      lengthMetric("diagonal", diagonal),
    ],
    formula: "A = s²",
    steps: [`A = s² = ${sub(s, c)}² = ${exactText(area, c)}`],
  };
}

function rectangleArea(r: Record<string, Rational>, c: Context): Draft {
  const { length: l, width: w } = r;
  const area = exactRational(mulR(l, w));
  const perimeter = mulR(intR(2), addR(l, w));
  return {
    ...exactArea(area),
    metrics: [
      lengthMetric("perimeter", exactRational(perimeter)),
      lengthMetric("diagonal", exactSqrt(addR(square(l), square(w)))),
    ],
    formula: "A = l × w",
    steps: [
      `A = l × w = ${text(l, c)} × ${text(w, c)} = ${exactText(area, c)}`,
    ],
  };
}

function triangleArea(r: Record<string, Rational>, c: Context): Draft {
  const { base: b, height: h } = r;
  const area = exactRational(mulR(HALF, mulR(b, h)));
  return {
    ...exactArea(area),
    metrics: [],
    formula: "A = ½ × b × h",
    steps: [
      `A = ½ × b × h = ½ × ${text(b, c)} × ${text(h, c)} = ${exactText(area, c)}`,
    ],
  };
}

function heronArea(r: Record<string, Rational>, c: Context): Draft | null {
  const { sideA: a, sideB: b, sideC: sideLength } = r;
  const perimeter = addR(addR(a, b), sideLength);
  const s = mulR(HALF, perimeter);
  const inside = mulR(
    mulR(s, subR(s, a)),
    mulR(subR(s, b), subR(s, sideLength))
  );
  if (signR(inside) <= 0) {
    return null;
  }
  const area = exactSqrt(inside);
  const insideText =
    inside.den === BigInt(1) ? text(inside, c) : `(${formatFraction(inside)})`;
  return {
    ...exactArea(area),
    metrics: [
      lengthMetric("perimeter", exactRational(perimeter)),
      lengthMetric("semiperimeter", exactRational(s)),
    ],
    formula: "s = (a + b + c) / 2,  A = √(s(s − a)(s − b)(s − c))",
    steps: [
      `s = (a + b + c) / 2 = (${text(a, c)} + ${text(b, c)} + ${text(sideLength, c)}) / 2 = ${text(s, c)}`,
      `A = √(s(s − a)(s − b)(s − c)) = √(${text(s, c)} × ${text(subR(s, a), c)} × ${text(subR(s, b), c)} × ${text(subR(s, sideLength), c)}) = √${insideText} = ${exactText(area, c)}`,
    ],
  };
}

function parallelogramArea(r: Record<string, Rational>, c: Context): Draft {
  const { base: b, height: h } = r;
  const area = exactRational(mulR(b, h));
  return {
    ...exactArea(area),
    metrics: [],
    formula: "A = b × h",
    steps: [
      `A = b × h = ${text(b, c)} × ${text(h, c)} = ${exactText(area, c)}`,
    ],
  };
}

function trapezoidArea(r: Record<string, Rational>, c: Context): Draft {
  const { base1: a, base2: b, height: h } = r;
  const midsegment = mulR(HALF, addR(a, b));
  const area = exactRational(mulR(midsegment, h));
  return {
    ...exactArea(area),
    metrics: [lengthMetric("midsegment", exactRational(midsegment))],
    formula: "A = ½ × (a + b) × h",
    steps: [
      `A = ½ × (a + b) × h = ½ × (${text(a, c)} + ${text(b, c)}) × ${text(h, c)} = ${exactText(area, c)}`,
    ],
  };
}

function rhombusArea(r: Record<string, Rational>, c: Context): Draft {
  const { diag1: p, diag2: q } = r;
  const area = exactRational(mulR(HALF, mulR(p, q)));
  const side = exactSqrt(
    mulR(divR(intR(1), intR(4)), addR(square(p), square(q)))
  );
  return {
    ...exactArea(area),
    metrics: [
      lengthMetric("sideLength", side),
      lengthMetric("perimeter", scaleExact(side, intR(4))),
    ],
    formula: "A = ½ × d₁ × d₂",
    steps: [
      `A = ½ × d₁ × d₂ = ½ × ${text(p, c)} × ${text(q, c)} = ${exactText(area, c)}`,
    ],
  };
}

function ellipseArea(r: Record<string, Rational>, c: Context): Draft {
  const { semiA: a, semiB: b } = r;
  const area = exactPi(mulR(a, b));
  const av = toNumberR(a);
  const bv = toNumberR(b);
  const approx =
    Math.PI * (3 * (av + bv) - Math.sqrt((3 * av + bv) * (av + 3 * bv)));
  return {
    ...exactArea(area),
    metrics: [lengthMetric("perimeterApprox", null, approx)],
    formula: "A = π × a × b",
    steps: [
      `A = π × a × b = π × ${text(a, c)} × ${text(b, c)} = ${exactText(area, c)}`,
    ],
  };
}

function annulusArea(r: Record<string, Rational>, c: Context): Draft {
  const { outer, inner } = r;
  const area = exactPi(subR(square(outer), square(inner)));
  return {
    ...exactArea(area),
    metrics: [lengthMetric("width", exactRational(subR(outer, inner)))],
    formula: "A = π(R² − r²)",
    steps: [
      `A = π(R² − r²) = π(${sub(outer, c)}² − ${sub(inner, c)}²) = ${exactText(area, c)}`,
    ],
  };
}

function sectorArea(
  r: Record<string, Rational>,
  angle: ParsedAngle,
  c: Context
): Draft {
  const radius = r.radius;
  const arc = mulExact(exactRational(radius), angle.radians);
  const area = scaleExact(
    mulExact(exactRational(square(radius)), angle.radians),
    HALF
  );
  const perimeter = addExact(exactRational(mulR(intR(2), radius)), arc);
  const angleLine =
    angle.degrees === null
      ? `θ = ${angle.radianText} rad ≈ ${decimalText(angle.degreesValue, c)}°`
      : `θ = ${text(angle.degrees, c)}° = ${angle.radianText} rad`;
  const theta = angle.radianText.includes("/")
    ? `(${angle.radianText})`
    : angle.radianText;
  return {
    ...exactArea(area),
    metrics: [
      lengthMetric("arcLength", arc),
      lengthMetric("perimeter", perimeter),
      {
        key: "angleDegrees",
        exact: null,
        value: angle.degreesValue,
        kind: "angle",
      },
    ],
    formula: "A = ½ × r² × θ  (θ in radians)",
    steps: [
      angleLine,
      `A = ½ × r² × θ = ½ × ${sub(radius, c)}² × ${theta} = ${exactText(area, c)}`,
    ],
  };
}

interface PolygonFactors {
  area: Exact;
  apothem: Exact;
  circumradius: Exact | null;
}

const root = (n: number): Exact => exactSqrt(intR(n));
const fraction = (num: number, den: number): Rational =>
  divR(intR(num), intR(den));

/** Exact area (× s²), apothem and circumradius (× s) for the constructible small cases. */
function exactPolygonFactors(n: number): PolygonFactors | null {
  switch (n) {
    case 3:
      return {
        area: scaleExact(root(3), fraction(1, 4)),
        apothem: scaleExact(root(3), fraction(1, 6)),
        circumradius: scaleExact(root(3), fraction(1, 3)),
      };
    case 4:
      return {
        area: exactRational(intR(1)),
        apothem: exactRational(fraction(1, 2)),
        circumradius: scaleExact(root(2), fraction(1, 2)),
      };
    case 6:
      return {
        area: scaleExact(root(3), fraction(3, 2)),
        apothem: scaleExact(root(3), fraction(1, 2)),
        circumradius: exactRational(intR(1)),
      };
    case 8:
      return {
        area: addExact(exactRational(intR(2)), scaleExact(root(2), intR(2))),
        apothem: scaleExact(
          addExact(exactRational(intR(1)), root(2)),
          fraction(1, 2)
        ),
        circumradius: null,
      };
    case 12:
      return {
        area: addExact(exactRational(intR(6)), scaleExact(root(3), intR(3))),
        apothem: scaleExact(
          addExact(exactRational(intR(2)), root(3)),
          fraction(1, 2)
        ),
        circumradius: scaleExact(addExact(root(6), root(2)), fraction(1, 2)),
      };
    default:
      return null;
  }
}

function polygonArea(
  r: Record<string, Rational>,
  n: number,
  c: Context
): Draft {
  const s = r.side;
  const sv = toNumberR(s);
  const tan = Math.tan(Math.PI / n);
  const areaValue = (n * sv * sv) / (4 * tan);
  const apothemValue = sv / (2 * tan);
  const circumradiusValue = sv / (2 * Math.sin(Math.PI / n));
  const factors = exactPolygonFactors(n);
  const perimeter = mulR(intR(n), s);
  const interior = divR(mulR(intR(n - 2), intR(180)), intR(n));
  const area = factors
    ? mulExact(factors.area, exactRational(square(s)))
    : null;
  const apothem = factors ? mulExact(factors.apothem, exactRational(s)) : null;
  const circumradius = factors?.circumradius
    ? mulExact(factors.circumradius, exactRational(s))
    : null;
  const answer = area ? exactText(area, c) : decimalText(areaValue, c);
  const equals = area ? "=" : "≈";
  return {
    area,
    areaValue,
    metrics: [
      lengthMetric("perimeter", exactRational(perimeter)),
      lengthMetric("apothem", apothem, apothemValue),
      lengthMetric("circumradius", circumradius, circumradiusValue),
      {
        key: "interiorAngle",
        exact: exactRational(interior),
        value: toNumberR(interior),
        kind: "angle",
      },
      {
        key: "centralAngle",
        exact: exactRational(divR(intR(360), intR(n))),
        value: 360 / n,
        kind: "angle",
      },
    ],
    formula: "A = n × s² / (4 × tan(180° / n))",
    steps: [
      `A = n × s² / (4 × tan(180° / n)) = ${n} × ${sub(s, c)}² / (4 × tan(${text(divR(intR(180), intR(n)), c)}°)) ${equals} ${answer}`,
    ],
  };
}

function allPositive(values: readonly (Rational | undefined)[]): boolean {
  return values.every((value) => value !== undefined && signR(value) > 0);
}

function sectorAngleValid(angle: ParsedAngle | undefined): boolean {
  if (!angle) {
    return false;
  }
  return (
    angle.degreesValue > 0 &&
    angle.degreesValue <= FULL_TURN_DEGREES + ANGLE_TOLERANCE
  );
}

function polygonSidesValid(sides: number | undefined): sides is number {
  return (
    sides !== undefined &&
    Number.isInteger(sides) &&
    sides >= MIN_POLYGON_SIDES &&
    sides <= MAX_POLYGON_SIDES
  );
}

function withLengths(
  shape: AreaShapeId,
  inputs: AreaInputs,
  context: Context
): Draft | AreaError {
  const fields = AREA_SHAPE_FIELDS[shape];
  const r = inputs.lengths as Record<string, Rational>;
  if (!allPositive(fields.map((id) => inputs.lengths[id]))) {
    return "positive";
  }
  switch (shape) {
    case "square":
      return squareArea(r, context);
    case "rectangle":
      return rectangleArea(r, context);
    case "triangle":
      return triangleArea(r, context);
    case "parallelogram":
      return parallelogramArea(r, context);
    case "trapezoid":
      return trapezoidArea(r, context);
    case "rhombus":
      return rhombusArea(r, context);
    case "ellipse":
      return ellipseArea(r, context);
    case "triangle3":
      return heronArea(r, context) ?? "triangle";
    case "annulus":
      return signR(subR(r.outer, r.inner)) > 0
        ? annulusArea(r, context)
        : "annulus";
    case "polygon":
      return polygonSidesValid(inputs.sides)
        ? polygonArea(r, inputs.sides, context)
        : "sides";
    case "sector":
      return sectorAngleValid(inputs.angle) && inputs.angle
        ? sectorArea(r, inputs.angle, context)
        : "angle";
    default:
      return "positive";
  }
}

/** The area of a shape from its measurements, with the working. */
export function computeArea(
  shape: AreaShapeId,
  inputs: AreaInputs,
  format: ResultFormat = DEFAULT_FORMAT
): AreaResult | AreaFailure {
  const outcome = withLengths(shape, inputs, format);
  if (typeof outcome === "string") {
    return { ok: false, error: outcome };
  }
  return { ok: true, ...outcome };
}
